import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { ActionLink, ActionsCell, EmptyState, SectionCard, StatusPill } from "./shared";
import { ledgerGroupLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

const ROOT_TYPE_LABELS = { ASSET: "Assets", LIABILITY: "Liabilities", FUND: "Funds / Capital", INCOME: "Income", EXPENSE: "Expenses" };
const ROOT_TYPE_ORDER = ["ASSET", "LIABILITY", "FUND", "INCOME", "EXPENSE"];

/**
 * Real Chart of Accounts, built from the actual Phase 3 engine data - Account
 * Groups (/engine/account-groups) nested under one of the 5 root types, with
 * every Ledger (/engine/ledgers) filed under its accountGroupId. Nothing
 * here is hardcoded: a Society with no custom groups still sees the 5 system
 * root groups (seeded once, shared by every Society) and its ledgers listed
 * directly under whichever root type their ledgerGroup implies.
 */
export default function ChartOfAccountsPage() {
  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [groupsRes, ledgersRes] = await Promise.all([
        api.get("/society/accounting/engine/account-groups").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers?includeInactive=true").catch(() => ({ data: [] })),
      ]);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setLedgers(Array.isArray(ledgersRes.data) ? ledgersRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load chart of accounts"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fallbackRootType = { bank: "ASSET", cash: "ASSET", asset: "ASSET", member: "ASSET", income: "INCOME", expense: "EXPENSE", vendor: "LIABILITY", liability: "LIABILITY", fund: "FUND", equity: "FUND", other: "ASSET" };

  const tree = useMemo(() => {
    const rootGroups = groups.filter((g) => !g.parentGroupId);
    const childGroups = groups.filter((g) => g.parentGroupId);
    return ROOT_TYPE_ORDER.map((rootType) => {
      const roots = rootGroups.filter((g) => g.rootType === rootType);
      const groupsWithLedgers = roots.map((g) => ({
        group: g,
        children: childGroups.filter((c) => c.parentGroupId === g.id),
        ledgers: ledgers.filter((l) => l.accountGroupId === g.id),
      }));
      const ungroupedLedgers = ledgers.filter((l) => !l.accountGroupId && (fallbackRootType[l.ledgerGroup] || "ASSET") === rootType);
      return { rootType, groupsWithLedgers, ungroupedLedgers };
    });
  }, [groups, ledgers]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (loading) return <div className="text-sm text-[#6B7280]">Loading chart of accounts...</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Chart of Accounts</h2>
          <p className="mt-1 text-sm text-[#6B7280]">The real account hierarchy - Assets, Liabilities, Funds / Capital, Income and Expenses - loaded from Account Groups and Ledgers.</p>
        </div>
        <Link to={`${ACCOUNTING_BASE}/ledgers`} className="rounded-sm border border-[#E5E7EB] px-3 py-1.5 text-sm font-semibold text-[#111827] hover:bg-[#F9FAFB]">Manage ledgers</Link>
      </div>

      <div className="space-y-3">
        {tree.map(({ rootType, groupsWithLedgers, ungroupedLedgers }) => (
          <SectionCard key={rootType} title={ROOT_TYPE_LABELS[rootType]}>
            {groupsWithLedgers.length === 0 && ungroupedLedgers.length === 0 ? (
              <EmptyState>No ledgers yet under {ROOT_TYPE_LABELS[rootType]}.</EmptyState>
            ) : (
              <div className="space-y-1">
                {groupsWithLedgers.map(({ group, children, ledgers: groupLedgers }) => (
                  <div key={group.id} className="rounded-sm border border-[#F3F4F6]">
                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                      <button type="button" onClick={() => toggle(group.id)} className="flex items-center gap-1.5 text-sm font-semibold text-[#111827]">
                        {expanded[group.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {group.name}
                        {group.isSystem && <span className="ml-1 rounded-sm bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#6B7280]">System</span>}
                        {group.active === false && <StatusPill active={false} />}
                      </button>
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/chart-of-accounts/${group.id}`}>View Group</ActionLink>
                        {!group.isSystem && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink to={`${ACCOUNTING_BASE}/chart-of-accounts/${group.id}`}>Edit</ActionLink>
                          </>
                        )}
                        <span className="text-[#D1D5DB]">|</span>
                        <ActionLink to={`${ACCOUNTING_BASE}/ledgers?group=${group.id}`}>Create Ledger under Group</ActionLink>
                      </ActionsCell>
                    </div>
                    {expanded[group.id] && (
                      <div className="border-t border-[#F3F4F6] px-3 py-2 pl-8">
                        {children.map((child) => <div key={child.id} className="text-xs font-semibold uppercase tracking-[0.1em] text-[#9CA3AF]">{child.name}</div>)}
                        {groupLedgers.length === 0 ? (
                          <div className="py-1 text-sm text-[#9CA3AF]">No ledgers in this group yet.</div>
                        ) : groupLedgers.map((l) => (
                          <div key={l.id} className="flex items-center justify-between py-1 text-sm">
                            <Link to={`${ACCOUNTING_BASE}/ledgers/${l.id}`} className="text-[#111827] hover:underline">{l.name}</Link>
                            <span className="text-xs text-[#9CA3AF]">{ledgerGroupLabels[l.ledgerGroup] || l.ledgerGroup}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {ungroupedLedgers.length > 0 && (
                  <div className="rounded-sm border border-dashed border-[#E5E7EB] px-3 py-2">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-[0.1em] text-[#9CA3AF]">Not yet filed under a group</div>
                    {ungroupedLedgers.map((l) => (
                      <div key={l.id} className="flex items-center justify-between py-1 text-sm">
                        <Link to={`${ACCOUNTING_BASE}/ledgers/${l.id}`} className="text-[#111827] hover:underline">{l.name}</Link>
                        <span className="text-xs text-[#9CA3AF]">{ledgerGroupLabels[l.ledgerGroup] || l.ledgerGroup}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        ))}
      </div>
    </div>
  );
}
